import { Badge, Tooltip } from "@jhool-io/fe-components";
import {
    makeStringFirstLetterCapital,
    removeEnumUnderscore,
} from "../../../utils/helpers";
import {
    ChangeRequestOutcome,
    ChangeRequestSource,
    ChangeRequestStatus,
    FlaggedNoteForAuditStatus,
    INoteUnitCalculation,
    NoteLabels,
    NoteStatus,
    NoteTypes,
    Source,
} from "../types/notes.types";
import { CPT_CODES_DESCRIPTION } from "../../../utils/constants";
import InfoIcon from "../../../icons/Info";
import type { IProvider } from "../../providers/types/providers.types";
import {
    UserPractice,
    UserRole,
} from "../../user-management/types/user-management.types";

type AuthorOption = Pick<IProvider, "provider_name" | "user_id">;
type NoteWithAuthor = {
    author: {
        first_name: string;
        id: string;
        last_name: string;
    };
};

function CptCodeWithDescription({
    code,
    category,
}: {
    code: string;
    category?: string;
}) {
    const description = CPT_CODES_DESCRIPTION[code];
    const formattedCode = removeEnumUnderscore(code);
    const tooltipContent = category
        ? `${category}: ${description ?? formattedCode}`
        : description;

    return tooltipContent ? (
        <Tooltip content={tooltipContent}>
            <span className="inline-flex items-center gap-1">
                {formattedCode}
                <InfoIcon />
            </span>
        </Tooltip>
    ) : (
        <span>{formattedCode}</span>
    );
}

const PSYCH_NOTE_TYPES: NoteTypes[] = [
    NoteTypes.TESTING_INTAKE_MINOR_INTAKE_PSYCH,
    NoteTypes.INTEGRATING_PATIENT_DATA_PSYCH,
    NoteTypes.ADMINISTRATION_PSYCH,
    NoteTypes.ADMINISTRATION_TECH,
    NoteTypes.SCORING_BY_TECH_PSYCH,
    NoteTypes.SCORING_BY_PSYCH,
    NoteTypes.INTERPRETATION_REPORT_WRITING_PSYCH,
    NoteTypes.FEEDBACK_PSYCH,
    NoteTypes.PAUSE_NOTIFICATION_PSYCH,
    NoteTypes.TERMINATION_OF_TREATMENT_PSYCH,
    NoteTypes.RECORD_OF_DISCLOSURE_PSYCH,
    NoteTypes.NO_SHOW_PSYCH,
    NoteTypes.COMMUNICATION_WITH_CLIENT,
];

const MED_NOTE_TYPES: NoteTypes[] = [
    NoteTypes.CONSULTATION_NOTE_MED,
    NoteTypes.DOCUMENTATION_REQUEST_NOTE_MED,
    NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED,
    NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED,
    NoteTypes.EVALUATION_SUBMISSION_NOTE_MED,
    NoteTypes.INTAKE_NOTE_MED,
    NoteTypes.NUTRITION_NOTE_MED,
    NoteTypes.NO_SHOW_NOTE_MED,
    NoteTypes.TERM_OF_CARE_NOTE_MED,
    NoteTypes.TREATMENT_REVIEW_NOTE_MED,
];

const PDF_EXPORTABLE_NOTE_TYPES = Object.values(NoteTypes);

export const isNotePdfExportable = (note: {
    note_type: NoteTypes;
    status: NoteStatus;
    note_url?: string;
    source?: Source;
}) => {
    const noteUrlWithoutQuery = note.note_url?.split("?")[0].toLowerCase();
    const isManualNote =
        note.source === Source.MANUAL ||
        Boolean(noteUrlWithoutQuery && !noteUrlWithoutQuery.endsWith(".json"));

    return (
        note.status !== NoteStatus.DRAFT &&
        (isManualNote || PDF_EXPORTABLE_NOTE_TYPES.includes(note.note_type))
    );
};

export const getPdfExportableNoteIds = (
    notes: Array<{
        note_id: string;
        note_type: NoteTypes;
        status: NoteStatus;
        note_url?: string;
        source?: Source;
    }>
) => notes.filter(isNotePdfExportable).map((note) => note.note_id);

export const getNoteStatusTag = (status: NoteStatus, text?: string) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case NoteStatus.CODED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case NoteStatus.DRAFT:
            bgColor = "var(--color-neutral-bg)";
            textColor = "var(--color-neutral-text)";
            break;
        case NoteStatus.CHANGES_REQUESTED:
            textColor = "var(--color-info-text)";
            bgColor = "var(--color-info-bg)";
            break;
        case NoteStatus.PENDING_CODE_REVIEW:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        case NoteStatus.PENDING_SUPERVISOR_REVIEW:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        default:
            bgColor = "var(--color-neutral-bg)";
            textColor = "var(--color-neutral-text)";
    }

    return (
        <Badge
            content={`${text ? `${text} ` : ``}${makeStringFirstLetterCapital(
                removeEnumUnderscore(status)
            )}`}
            type="custom"
            textColor={textColor}
            bgColor={bgColor}
        />
    );
};

export const getNoteListNavigateUrl = (userRoles: string[]) => {
    // Get note list filters from localstorage
    // This is a string of url searchParams `status%5B%5D=pending_supervisor_review&status%5B%5D=coded&status%5B%5D=changes_requested`
    const noteListFiltersFromLS = localStorage.getItem("notelist_filters");

    if (localStorage.getItem("activeBtn") === "my_supervisees_notes") {
        return `/notes?tab=${localStorage.getItem("activeBtn")}`;
    }

    if (noteListFiltersFromLS) {
        return `/notes?${noteListFiltersFromLS}`;
    }

    if (userRoles.includes("provider")) {
        return `/notes?status[]=${NoteStatus.PENDING_SUPERVISOR_REVIEW}&status[]=${NoteStatus.DRAFT}&status[]=${NoteStatus.CHANGES_REQUESTED}&status[]=${NoteStatus.PENDING_CODE_REVIEW}`;
    }
    if (userRoles.includes("coder") || userRoles.includes("biller")) {
        return `/notes?status[]=${NoteStatus.PENDING_CODE_REVIEW}`;
    }
    return `/notes?status[]=${NoteStatus.PENDING_SUPERVISOR_REVIEW}&status[]=${NoteStatus.CODED}&status[]=${NoteStatus.CHANGES_REQUESTED}&status[]=${NoteStatus.PENDING_CODE_REVIEW}`;
};

export const getChangeRequestSources = (
    practice: Pick<UserPractice, "roles" | "is_supervisor"> | null | undefined
): ChangeRequestSource[] => {
    if (!practice) return [];
    const { roles } = practice;
    if (roles.includes(UserRole.SUPER_ADMIN)) {
        return Object.values(ChangeRequestSource);
    }
    const sources: ChangeRequestSource[] = [];
    if (
        [UserRole.AUDITOR, UserRole.AUDITOR_ADMIN].some((role) =>
            roles.includes(role)
        )
    ) {
        sources.push(ChangeRequestSource.AUDIT);
    }
    if (practice.is_supervisor && roles.includes(UserRole.PROVIDER)) {
        sources.push(ChangeRequestSource.SUPERVISOR);
    }
    if (
        [UserRole.BILLER, UserRole.BILLER_ADMIN, UserRole.CODER].some((role) =>
            roles.includes(role)
        )
    ) {
        sources.push(ChangeRequestSource.BILLING);
    }
    return sources;
};

export const changeRequestOutcomeLabels: Record<ChangeRequestOutcome, string> =
    {
        [ChangeRequestOutcome.CLINICIAN_CORRECTED]: "Clinician corrected",
        [ChangeRequestOutcome.FLAGGED_IN_ERROR]: "Flagged in error",
    };

export const getChangeRequestStatusTag = (status: ChangeRequestStatus) => {
    switch (status) {
        case ChangeRequestStatus.OPEN:
            return <Badge type="warning" content="Awaiting addendum" />;
        case ChangeRequestStatus.ANSWERED:
            return <Badge type="info" content="Answered" />;
        default:
            return <Badge type="neutral" content="Closed" />;
    }
};

export const getFlaggedNoteListNavigateUrl = () => {
    const flaggedNotesFiltersFromLS = localStorage.getItem(
        "flaggednotes_filters"
    );

    if (flaggedNotesFiltersFromLS) {
        return `/notes/flagged-notes?${flaggedNotesFiltersFromLS}`;
    }

    return `/notes/flagged-notes?flagged_status[]=${FlaggedNoteForAuditStatus.UNFLAGGED}&flagged_status[]=${FlaggedNoteForAuditStatus.FLAGGED}&flagged_status[]=${FlaggedNoteForAuditStatus.REQUIRES_ACTION}&flagged_status[]=${FlaggedNoteForAuditStatus.SENT_FOR_CHANGES}`;
};

export const getFlaggedNoteStatusTag = (status: FlaggedNoteForAuditStatus) => {
    let bgColor: string;
    let textColor: string;

    switch (status) {
        case FlaggedNoteForAuditStatus.UNFLAGGED:
            textColor = "var(--color-success-text)";
            bgColor = "var(--color-success-bg)";
            break;
        case FlaggedNoteForAuditStatus.REQUIRES_ACTION:
            textColor = "var(--color-info-text)";
            bgColor = "var(--color-info-bg)";
            break;
        case FlaggedNoteForAuditStatus.FLAGGED:
            textColor = "var(--color-danger-text)";
            bgColor = "var(--color-danger-bg)";
            break;
        case FlaggedNoteForAuditStatus.SENT_FOR_CHANGES:
            textColor = "var(--color-warning-text)";
            bgColor = "var(--color-warning-bg)";
            break;
        default:
            bgColor = "var(--color-neutral-bg)";
            textColor = "var(--color-neutral-text)";
    }

    return (
        <Badge
            content={makeStringFirstLetterCapital(removeEnumUnderscore(status))}
            type="custom"
            textColor={textColor}
            bgColor={bgColor}
        />
    );
};

export const getNoteTypeTextWithTag = (type: NoteTypes) => {
    if (PSYCH_NOTE_TYPES.includes(type)) {
        return (
            <span className="inline-flex items-center gap-x-1">
                {NoteLabels[type].replace("(psych)", "")}
                <span>
                    <Badge type="info" content="PSYCH" />
                </span>
            </span>
        );
    }

    if (MED_NOTE_TYPES.includes(type)) {
        return (
            <span className="inline-flex items-center gap-x-1">
                {NoteLabels[type].replace("(med)", "")}
                <Badge type="success" content="MED" />
            </span>
        );
    }

    return (
        <span className="inline-flex items-center gap-x-1">
            {NoteLabels[type]} <Badge type="neutral" content="THERAPY" />
        </span>
    );
};

export const isPsychNote = (type?: NoteTypes) =>
    type ? PSYCH_NOTE_TYPES.includes(type) : false;

export const isMedNote = (type?: NoteTypes) =>
    type ? MED_NOTE_TYPES.includes(type) : false;

export function getCptCodeToDisplay({
    noteType,
    cptCode,
    additionalCptCodes,
    unitCalculation,
}: {
    noteType: NoteTypes;
    cptCode?: string;
    additionalCptCodes?: string[] | null;
    unitCalculation?: INoteUnitCalculation;
}) {
    if (noteType === NoteTypes.CANCELLATION_NOTE) {
        return "NO SHOW";
    }

    if (noteType === NoteTypes.FORMS_AND_DOCUMENTATION_NOTE) {
        return "FORMS & DOC";
    }

    if (
        (isPsychNote(noteType) || noteType === NoteTypes.NUTRITION_NOTE_MED) &&
        unitCalculation &&
        unitCalculation.unit_assignments &&
        unitCalculation.unit_assignments.length > 0
    ) {
        const cptCodes = unitCalculation.unit_assignments
            .map((assignment) => assignment.cpt_code?.trim())
            .filter((code): code is string => Boolean(code));

        if (cptCodes.length > 0) {
            const cptCodeCountMap = cptCodes.reduce((map, code) => {
                map.set(code, (map.get(code) ?? 0) + 1);
                return map;
            }, new Map<string, number>());

            return (
                <span className="flex flex-wrap items-center gap-2">
                    {Array.from(cptCodeCountMap.entries()).map(
                        ([code, count]) => (
                            <span
                                key={code}
                                className="inline-flex items-center gap-1"
                            >
                                <CptCodeWithDescription code={code} />
                                {count > 1 ? (
                                    <span className="rounded-full bg-slate-100 px-2 py-0.5 text-xs text-slate-600">
                                        x {count}
                                    </span>
                                ) : null}
                            </span>
                        )
                    )}
                </span>
            );
        }
    }

    const normalizedAdditionalCptCodes = (additionalCptCodes ?? [])
        .map((code) => code.trim())
        .filter(Boolean)
        .filter((code) => code !== cptCode);

    if (normalizedAdditionalCptCodes.length > 0) {
        const primaryCategory =
            noteType === NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED ||
            noteType === NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED
                ? "E/M CPT code"
                : "Primary CPT code";
        let additionalCategory = "Additional CPT code";
        if (noteType === NoteTypes.EVALUATION_MANAGEMENT_AND_THERAPY_NOTE_MED) {
            additionalCategory = "Therapy add-on CPT code";
        } else if (noteType === NoteTypes.EVALUATION_MANAGEMENT_NOTE_MED) {
            additionalCategory = "Ketamine administration CPT code";
        }

        return (
            <span className="flex flex-col items-start gap-1">
                {cptCode ? (
                    <CptCodeWithDescription
                        code={cptCode}
                        category={primaryCategory}
                    />
                ) : (
                    <span>--</span>
                )}
                {normalizedAdditionalCptCodes.map((code) => (
                    <CptCodeWithDescription
                        key={code}
                        code={code}
                        category={additionalCategory}
                    />
                ))}
            </span>
        );
    }

    return cptCode ? <CptCodeWithDescription code={cptCode} /> : "--";
}

export function getSelectedAuthorName(
    providers: AuthorOption[] | undefined,
    notes: NoteWithAuthor[] | undefined,
    authorId: string,
    persistedAuthorName: string
) {
    if (!authorId) return "";

    const providerName = providers?.find(
        (provider) => provider.user_id === authorId
    )?.provider_name;
    if (providerName) return providerName;

    const noteAuthor = notes?.find(
        (note) => note.author.id === authorId
    )?.author;
    if (noteAuthor) {
        return `${noteAuthor.first_name} ${noteAuthor.last_name}`;
    }

    return persistedAuthorName;
}

export function updateAuthorFilterSearchParams(
    currentSearchParams: URLSearchParams,
    authorId: string,
    authorName = ""
) {
    const nextSearchParams = new URLSearchParams(currentSearchParams);

    if (authorId) {
        nextSearchParams.set("author", authorId);
        if (authorName) nextSearchParams.set("author_name", authorName);
        else nextSearchParams.delete("author_name");
    } else {
        nextSearchParams.delete("author");
        nextSearchParams.delete("author_name");
    }

    nextSearchParams.set("page", "1");
    return nextSearchParams;
}
